import { apiSlice } from '@/api'
import { adminServiceApiSlice } from '@/api/adminServiceApi'
import { combineDuplicatedRecognitionsWithCount } from '@/utils/dedupeRecognitions'

export type PhotoNumberQueryArg = number | string

interface RecognitionImage {
  image_base_64: string
  s3_url: string
  full_image_base_64: string
}

export const enteranceApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    openGate: builder.query({
      query: (args) => {
        const { channelId, reason } = args || {}
        return {
          url: `api/v2/open/${channelId}`,
          method: 'GET',
          params: reason ? { reason } : undefined
        }
      }
    }),
    closeGate: builder.query({
      query: (channelId) => ({
        url: `api/v2/close/${channelId}`,
        method: 'GET'
      })
    }),
    getInfo: builder.query({
      query: (arg) => ({
        url: `api/v2/recognitions/history`,
        method: 'GET',
        params: arg
      }),
      providesTags: ['Recognitions']
    }),
    getPhotoNumber: builder.query<RecognitionImage, PhotoNumberQueryArg>({
      query: (id) => ({
        url: `api/v2/recognitions/image/${id}`,
        method: 'GET'
      }),
      extraOptions: { maxRetries: 0 },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg !== previousArg
      }
    }),
    updateRecognition: builder.mutation({
      query: (body) => ({
        url: 'api/v2/recognitions/update',
        method: 'POST',
        body
      }),
      invalidatesTags: ['Recognitions'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          dispatch(adminServiceApiSlice.util.invalidateTags(['LastEntries']))
        } catch {
          // keep edit modal error state
        }
      }
    }),
    customAddNumber: builder.mutation({
      query: (body) => ({
        url: 'api/v2/entrance/manual',
        method: 'POST',
        body
      }),
      invalidatesTags: ['Recognitions']
    }),
    getChannelById: builder.query({
      query: (channelId) => ({
        url: `api/v2/channels/${channelId}`,
        method: 'GET'
      }),
      extraOptions: { maxRetries: 0 }
    }),
    getLastEntriesByChannel: builder.query({
      query: ({ channelId, page = 1, size = 50, search = '' }) => ({
        url: `api/v2/last_entries/channel/${channelId}`,
        method: 'GET',
        params: { page, size, search }
      }),
      transformResponse: (response) => ({
        ...response,
        data: combineDuplicatedRecognitionsWithCount(response.data)
      }),
      keepUnusedDataFor: 0,
      providesTags: (_result, _error, { channelId, page }) => [
        { type: 'ChannelEntries', id: `${channelId}-${page}` }
      ]
    }),
  })
})

export const {
  useLazyOpenGateQuery,
  useLazyCloseGateQuery,
  useLazyGetInfoQuery,
  useGetInfoQuery,
  useLazyGetPhotoNumberQuery,
  useGetPhotoNumberQuery,
  useUpdateRecognitionMutation,
  useCustomAddNumberMutation,
  useGetChannelByIdQuery,
  useGetLastEntriesByChannelQuery,
  useLazyGetLastEntriesByChannelQuery,
} = enteranceApi
